import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Search,
  TrendingUp,
} from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { useLocations } from '../../hooks/locations/useLocations'
import { useLocationDepartments } from '../../hooks/locations/useLocationPointConfig'
import { useRevenueVsPayrollReport } from '../../hooks/useReports'
import type {
  RevenueVsPayrollDay,
  RevenueVsPayrollFilters,
  RevenueVsPayrollResponse,
} from '../../types/reports.types'

function formatKzt(n: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(n)
}

function formatKztShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}М`
  if (n >= 1_000) return `${Math.round(n / 1_000)}к`
  return String(n)
}

function formatDateDM(iso: string): string {
  // iso = YYYY-MM-DD → "DD.M"
  const [, m, d] = iso.split('-')
  return `${d}.${parseInt(m, 10)}`
}

function formatDateRu(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

function defaultMonth(): { year: number; month: number } {
  const d = new Date()
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1)
  return { year: prev.getFullYear(), month: prev.getMonth() + 1 }
}

type SortKey = 'date' | 'revenue' | 'payroll' | 'ratio' | 'diff'
type SortDir = 'asc' | 'desc'

export function RevenueVsPayrollReport() {
  const init = defaultMonth()
  const [filters, setFilters] = useState<RevenueVsPayrollFilters>({
    locationId: undefined,
    year: init.year,
    month: init.month,
  })
  const [shouldFetch, setShouldFetch] = useState(false)

  const { data: locationsData } = useLocations({ limit: 200 })
  const locations = locationsData?.data ?? []

  // Подразделения точки: предвыбраны все, отдельные можно снять
  // (null = все подразделения, дефолт из настройки локации)
  const { data: locationDepts } = useLocationDepartments(filters.locationId)
  const [excludedDeptIds, setExcludedDeptIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setExcludedDeptIds(new Set())
    setShouldFetch(false)
  }, [filters.locationId])

  const toggleDept = (id: string) => {
    setExcludedDeptIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectedDeptIds = useMemo(
    () => (locationDepts ?? []).map((d) => d.id).filter((id) => !excludedDeptIds.has(id)),
    [locationDepts, excludedDeptIds]
  )
  const allDeptsSelected =
    !locationDepts || selectedDeptIds.length === locationDepts.length
  const noDeptsSelected =
    !!locationDepts && locationDepts.length > 0 && selectedDeptIds.length === 0

  const effectiveFilters = useMemo<RevenueVsPayrollFilters>(
    () => ({
      ...filters,
      departmentIds: allDeptsSelected ? undefined : [...selectedDeptIds].sort(),
    }),
    [filters, allDeptsSelected, selectedDeptIds]
  )

  const { data: report, isLoading, isFetching, error, refetch } =
    useRevenueVsPayrollReport(effectiveFilters, shouldFetch && !noDeptsSelected)

  const handleGenerate = () => {
    if (!filters.locationId || noDeptsSelected) return
    setShouldFetch(true)
    if (shouldFetch) refetch()
  }

  const periodValue = `${filters.year}-${String(filters.month).padStart(2, '0')}`
  const onPeriodChange = (value: string) => {
    const [y, m] = value.split('-')
    setFilters((p) => ({ ...p, year: parseInt(y, 10), month: parseInt(m, 10) }))
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="rvp-location">Локация</Label>
              <select
                id="rvp-location"
                value={filters.locationId ?? ''}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    locationId: e.target.value || undefined,
                  }))
                }
                className="h-10 w-[280px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Выберите локацию</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rvp-period">Период</Label>
              <input
                id="rvp-period"
                type="month"
                value={periodValue}
                onChange={(e) => onPeriodChange(e.target.value)}
                className="h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rvp-taxes">Налоги на ФОТ</Label>
              <label
                htmlFor="rvp-taxes"
                className="flex h-10 cursor-pointer items-center gap-2 text-sm"
              >
                <input
                  id="rvp-taxes"
                  type="checkbox"
                  checked={!!filters.includeTaxes}
                  onChange={(e) =>
                    setFilters((p) => ({
                      ...p,
                      includeTaxes: e.target.checked,
                    }))
                  }
                  className="h-4 w-4"
                />
                Добавить налоги (+11.5%)
              </label>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleGenerate}
                disabled={
                  !filters.locationId ||
                  noDeptsSelected ||
                  isLoading ||
                  isFetching
                }
                className="gap-2"
              >
                {isLoading || isFetching ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                Сформировать отчёт
              </Button>
            </div>
          </div>
          {!filters.locationId && (
            <p className="mt-3 text-sm text-muted-foreground">
              Выберите локацию. Точка продаж и подразделения точки
              настраиваются в карточке локации (Локации → Редактировать).
            </p>
          )}

          {filters.locationId && locationDepts && locationDepts.length > 0 && (
            <div className="mt-4">
              <Label>Подразделения точки (штат для ФОТ)</Label>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                {locationDepts.map((dept) => (
                  <label
                    key={dept.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={!excludedDeptIds.has(dept.id)}
                      onChange={() => toggleDept(dept.id)}
                      className="h-4 w-4"
                    />
                    {dept.name}
                  </label>
                ))}
              </div>
              {noDeptsSelected && (
                <p className="mt-2 text-sm text-red-500">
                  Выберите хотя бы одно подразделение
                </p>
              )}
            </div>
          )}

          {filters.locationId && locationDepts && locationDepts.length === 0 && (
            <p className="mt-3 text-sm text-amber-600">
              У локации не привязаны подразделения — штат будет посчитан по
              назначениям сотрудников на локацию (может быть завышен).
              Привяжите подразделения в карточке локации.
            </p>
          )}
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-2 text-red-600">
              <AlertCircle className="h-5 w-5 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Ошибка загрузки отчёта.</p>
                <p className="mt-1">
                  Возможные причины: у локации не настроен forecast department,
                  или aqniet API недоступен. Подробности в консоли.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {shouldFetch && report && !isLoading && !isFetching && (
        <ReportBody report={report} />
      )}

      {shouldFetch && (isLoading || isFetching) && (
        <Card>
          <CardContent className="pt-6">
            <LoadingTable rows={8} />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ReportBody({ report }: { report: RevenueVsPayrollResponse }) {
  const { data, meta } = report
  const firstDate = data[0]?.date ?? null
  const lastDate = data[data.length - 1]?.date ?? null
  const estimation = meta.bonuses_estimated ? meta.bonuses_estimation : null

  return (
    <>
      {/* KPI block */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <Kpi
              label={
                estimation
                  ? 'Среднее %ФОТ/Выручка (оценка)'
                  : 'Среднее %ФОТ/Выручка'
              }
              hint={
                meta.taxes_included
                  ? `с налогами +${meta.tax_percent}%`
                  : undefined
              }
              value={
                meta.total_ratio_percent !== null
                  ? `${meta.total_ratio_percent.toFixed(1)}%`
                  : '—'
              }
              accent="text-purple-600"
            />
            <Kpi
              label="Выручка за период"
              value={`${formatKzt(meta.total_revenue)} ₸`}
              accent="text-emerald-600"
            />
            <Kpi
              label="ФОТ за период"
              value={`${formatKzt(meta.total_payroll)} ₸`}
              accent="text-blue-600"
              hint={`оклад ${formatKzt(meta.total_salary)} + бонусы ${formatKzt(
                meta.total_bonuses,
              )}${estimation ? ' (оценка)' : ''}${
                meta.taxes_included ? `, включая налоги +${meta.tax_percent}%` : ''
              }`}
            />
            <Kpi
              label="Сотрудников"
              value={String(meta.employees_count)}
              accent="text-foreground"
            />
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Выручка vs ФОТ
          </CardTitle>
          <CardDescription>
            {meta.location_name ?? 'Локация'} | Период:{' '}
            {firstDate && lastDate
              ? `с ${formatDateRu(firstDate)} по ${formatDateRu(lastDate)}`
              : `${String(meta.month).padStart(2, '0')}.${meta.year}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RevenueVsPayrollLineChart data={data} />
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Подневная разбивка</CardTitle>
          <CardDescription>Найдено записей: {data.length}</CardDescription>
        </CardHeader>
        <CardContent>
          <DailyTable data={data} locationName={meta.location_name} />
        </CardContent>
      </Card>
    </>
  )
}

function Kpi({
  label,
  value,
  accent,
  hint,
}: {
  label: string
  value: string
  accent: string
  hint?: string
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent}`}>{value}</div>
      {hint && (
        <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
      )}
    </div>
  )
}

function RevenueVsPayrollLineChart({ data }: { data: RevenueVsPayrollDay[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const n = data.length

  const maxValue = useMemo(() => {
    const m = Math.max(0, ...data.map((d) => Math.max(d.revenue, d.payroll)))
    if (m <= 0) return 1
    // Round up to a tidy axis ceiling.
    const pow = Math.pow(10, Math.floor(Math.log10(m)))
    const step = pow / 2
    return Math.ceil(m / step) * step
  }, [data])

  const W = 1200
  const H = 380
  const padL = 60
  const padR = 24
  const padT = 24
  const padB = 36
  const plotW = W - padL - padR
  const plotH = H - padT - padB

  const xAt = (i: number) => padL + (n <= 1 ? plotW / 2 : (plotW * i) / (n - 1))
  const yAt = (v: number) => padT + plotH - (v / maxValue) * plotH

  const valueTicks = useMemo(() => {
    const out: number[] = []
    const stepCount = 4
    for (let i = 0; i <= stepCount; i++) {
      out.push((maxValue * i) / stepCount)
    }
    return out
  }, [maxValue])

  const revenuePoints = data
    .map((d, i) => `${xAt(i).toFixed(1)},${yAt(d.revenue).toFixed(1)}`)
    .join(' ')
  const payrollPoints = data
    .map((d, i) => `${xAt(i).toFixed(1)},${yAt(d.payroll).toFixed(1)}`)
    .join(' ')

  // Decide X-label cadence — every 2 days fits ~31 day months comfortably.
  const labelStride = n > 24 ? 2 : 1

  return (
    <div className="space-y-3">
      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          className="min-w-[700px]"
          role="img"
          aria-label="Выручка vs ФОТ"
        >
          {/* Gridlines + y-labels */}
          {valueTicks.map((tv, i) => (
            <g key={`vt-${i}`}>
              <line
                x1={padL}
                x2={W - padR}
                y1={yAt(tv)}
                y2={yAt(tv)}
                stroke="#e5e7eb"
                strokeWidth={1}
                strokeDasharray={i === 0 ? '' : '3 3'}
              />
              <text
                x={padL - 8}
                y={yAt(tv) + 4}
                textAnchor="end"
                fontSize={11}
                fill="#9ca3af"
              >
                {formatKztShort(tv)}
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {data.map((d, i) => {
            if (i % labelStride !== 0) return null
            return (
              <text
                key={`xl-${i}`}
                x={xAt(i)}
                y={H - padB + 18}
                textAnchor="middle"
                fontSize={11}
                fill="#9ca3af"
              >
                {formatDateDM(d.date)}
              </text>
            )
          })}

          {/* Hover capture (one column per day) */}
          {data.map((_, i) => {
            const halfSlot = n > 1 ? (plotW / (n - 1)) / 2 : plotW / 2
            return (
              <rect
                key={`hc-${i}`}
                x={xAt(i) - halfSlot}
                y={padT}
                width={halfSlot * 2}
                height={plotH}
                fill={hoverIdx === i ? 'rgba(99,102,241,0.06)' : 'transparent'}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              />
            )
          })}

          {/* Revenue line (green = Факт) */}
          <polyline
            fill="none"
            stroke="#10b981"
            strokeWidth={2}
            points={revenuePoints}
          />
          {data.map((d, i) => (
            <circle
              key={`rp-${i}`}
              cx={xAt(i)}
              cy={yAt(d.revenue)}
              r={hoverIdx === i ? 4.5 : 3}
              fill="#10b981"
              stroke="#fff"
              strokeWidth={1}
            />
          ))}

          {/* Payroll line (blue = ФОТ) */}
          <polyline
            fill="none"
            stroke="#3b82f6"
            strokeWidth={2}
            points={payrollPoints}
          />
          {data.map((d, i) => (
            <circle
              key={`pp-${i}`}
              cx={xAt(i)}
              cy={yAt(d.payroll)}
              r={hoverIdx === i ? 4.5 : 3}
              fill="#3b82f6"
              stroke="#fff"
              strokeWidth={1}
            />
          ))}

          {/* Axes */}
          <line
            x1={padL}
            x2={W - padR}
            y1={padT + plotH}
            y2={padT + plotH}
            stroke="#d1d5db"
            strokeWidth={1}
          />

          {/* Tooltip */}
          {hoverIdx !== null && data[hoverIdx] && (
            <g pointerEvents="none">
              <line
                x1={xAt(hoverIdx)}
                x2={xAt(hoverIdx)}
                y1={padT}
                y2={padT + plotH}
                stroke="#9ca3af"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            </g>
          )}
        </svg>

        {/* HTML tooltip (overlay) */}
        {hoverIdx !== null && data[hoverIdx] && (
          <div className="pointer-events-none absolute right-2 top-2 rounded-md border border-border bg-background/95 p-2 text-xs shadow-md">
            <div className="font-semibold">
              {formatDateRu(data[hoverIdx].date)}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
              Выручка:{' '}
              <span className="font-mono">
                {formatKzt(data[hoverIdx].revenue)} ₸
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
              ФОТ:{' '}
              <span className="font-mono">
                {formatKzt(data[hoverIdx].payroll)} ₸
              </span>
            </div>
            <div className="mt-1 border-t pt-1">
              %ФОТ/Выручка:{' '}
              <span className="font-mono">
                {data[hoverIdx].ratio_percent !== null
                  ? `${data[hoverIdx].ratio_percent!.toFixed(1)}%`
                  : '—'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
          ФОТ
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
          Выручка
        </span>
      </div>
    </div>
  )
}

function DailyTable({
  data,
  locationName,
}: {
  data: RevenueVsPayrollDay[]
  locationName: string | null
}) {
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set())
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set())

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }
  const toggleDay = (date: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev)
      if (next.has(date)) next.delete(date)
      else next.add(date)
      return next
    })
  }
  const toggleDept = (key: string) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const sorted = useMemo(() => {
    const rows = data.map((d) => ({
      ...d,
      diff: d.revenue - d.payroll,
    }))
    rows.sort((a, b) => {
      let av: number | string
      let bv: number | string
      switch (sortKey) {
        case 'date':
          av = a.date
          bv = b.date
          break
        case 'revenue':
          av = a.revenue
          bv = b.revenue
          break
        case 'payroll':
          av = a.payroll
          bv = b.payroll
          break
        case 'ratio':
          av = a.ratio_percent ?? -1
          bv = b.ratio_percent ?? -1
          break
        case 'diff':
          av = a.diff
          bv = b.diff
          break
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return rows
  }, [data, sortKey, sortDir])

  const arrow = (k: SortKey) =>
    sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8"></TableHead>
            <TableHead
              className="cursor-pointer select-none"
              onClick={() => toggleSort('date')}
            >
              Дата / Подразделение / Должность{arrow('date')}
            </TableHead>
            <TableHead className="text-center">Сотр. на смене</TableHead>
            <TableHead
              className="cursor-pointer select-none text-right"
              onClick={() => toggleSort('payroll')}
            >
              ФОТ{arrow('payroll')}
            </TableHead>
            <TableHead
              className="cursor-pointer select-none text-right"
              onClick={() => toggleSort('revenue')}
            >
              Выручка{arrow('revenue')}
            </TableHead>
            <TableHead
              className="cursor-pointer select-none text-right"
              onClick={() => toggleSort('diff')}
            >
              Разница{arrow('diff')}
            </TableHead>
            <TableHead
              className="cursor-pointer select-none text-right"
              onClick={() => toggleSort('ratio')}
            >
              % ФОТ/Выручка{arrow('ratio')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row) => {
            const isDayOpen = expandedDays.has(row.date)
            const ratioRed =
              row.ratio_percent !== null && row.ratio_percent > 50
            const dayHeadcount = row.departments.reduce(
              (acc, d) => acc + d.employees_count,
              0,
            )
            return (
              <Fragment key={row.date}>
                <TableRow
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => toggleDay(row.date)}
                >
                  <TableCell className="p-1 align-middle">
                    {isDayOpen ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">
                    {formatDateRu(row.date)}{' '}
                    <span className="text-muted-foreground text-xs">
                      ({locationName ?? '—'})
                    </span>
                  </TableCell>
                  <TableCell className="text-center font-mono">
                    {dayHeadcount}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatKzt(row.payroll)} ₸
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatKzt(row.revenue)} ₸
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${row.diff < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                  >
                    {row.diff < 0 ? '−' : ''}
                    {formatKzt(Math.abs(row.diff))} ₸
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${ratioRed ? 'text-red-600' : 'text-muted-foreground'}`}
                  >
                    {row.ratio_percent !== null
                      ? `${row.ratio_percent.toFixed(1)}%`
                      : '—'}
                  </TableCell>
                </TableRow>

                {isDayOpen &&
                  row.departments.map((dept) => {
                    const deptKey = `${row.date}::${dept.department_id ?? 'none'}`
                    const isDeptOpen = expandedDepts.has(deptKey)
                    return (
                      <Fragment key={deptKey}>
                        <TableRow
                          className="bg-muted/30 cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleDept(deptKey)}
                        >
                          <TableCell className="p-1 pl-4 align-middle">
                            {isDeptOpen ? (
                              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="pl-6 text-sm">
                            {dept.department_name ?? '—'}
                          </TableCell>
                          <TableCell className="text-center font-mono text-sm">
                            {dept.employees_count}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatKzt(dept.payroll)} ₸
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground"></TableCell>
                          <TableCell className="text-right text-muted-foreground"></TableCell>
                          <TableCell className="text-right text-muted-foreground"></TableCell>
                        </TableRow>

                        {isDeptOpen &&
                          dept.positions.map((pos) => (
                            <TableRow
                              key={`${deptKey}::${pos.position_id ?? 'none'}`}
                              className="bg-muted/10"
                            >
                              <TableCell></TableCell>
                              <TableCell className="pl-12 text-sm text-muted-foreground">
                                {pos.position_name ?? '—'}
                              </TableCell>
                              <TableCell className="text-center font-mono text-sm">
                                {pos.employees_count}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatKzt(pos.payroll)} ₸
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ))}
                      </Fragment>
                    )
                  })}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
